import { PlayArrowOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dataBase from "../../../assets/icons/database.svg";
import { AxiosError } from "axios";
import api from "../../../services/api";
import { useNotification } from "../../notificationProvider";
import QueryEditor from "./queryEditor";
import { ReactComponent as MySql } from "../../../assets/logosSvg/mySQL_icon.svg";
import { ReactComponent as Postgres } from "../../../assets/logosSvg/postgresql.svg";
import { ReactComponent as MariaDB } from "../../../assets/logosSvg/mariaDB_icon.svg";
import { RowsDataSource } from "../../administracao/types";
import VirtualizedSimpleTreeView from "./VirtualizedSimpleTreeView";
import { useFilters } from "../../../context/filterContext";
interface executeQueryProps {
  data: any;

  setResultQuery: React.Dispatch<SetStateAction<any[]>>;
  setErrorQuery: React.Dispatch<SetStateAction<string>>;
  setLoading: React.Dispatch<SetStateAction<boolean>>;
  initialQuery: string;
  handleUpdateGraphic: (newGraphic: DashGraphicsCards) => void;
  pageId: string;
}

const ExecuteQuery = (props: executeQueryProps) => {
  const {
    data,
    initialQuery,
    setResultQuery,
    setErrorQuery,
    setLoading,
    handleUpdateGraphic,
    pageId,
  } = props;

  const [open, setOpen] = useState(false);
  const [filterTable, setFilterTable] = useState<string>("");
  const [query, setQuery] = useState<string>(initialQuery);
  const [tables, setTables] = useState<
    Record<string, { columnName: string; type: string }[]>
  >({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const [graphicData, setGraphicData] = useState<DashGraphicsCards>(data);
  const [datasource, setDatasource] = useState<{ id: string } | null>(
    data?.dataSource,
  );
  const [dataSources, setDataSources] = useState<RowsDataSource[]>([]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const notify = useNotification();
  const { filters } = useFilters();
  const getFilteredRows = useMemo(() => {
    return Object.entries(tables)?.filter(([key]) => {
      return filterTable
        ? key?.toLowerCase().includes(filterTable.toLowerCase())
        : true;
    });
  }, [tables, filterTable]);

  const handleChangeGraph = useCallback(
    (key: string, value: string | unknown, isSuccess?: boolean) => {
      setGraphicData((prev: DashGraphicsCards | undefined) => {
        if (!prev) return prev;

        const updatedGraphic = {
          ...prev,
          [key]: value,
          error: isSuccess ? null : key === "error" ? value : prev.error,
        };
        handleUpdateGraphic(updatedGraphic);
        return updatedGraphic;
      });
    },
    [handleUpdateGraphic],
  );

  async function executeQuery() {
    if (!query || query.trim() === "") return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    const signal = controller.signal;

    abortControllerRef.current = controller;
    setLoading(true);

    try {
      const result = await api.post(
        "/pages/query",
        {
          query,
          pageId,
          filters,
          ...datasource,
        },
        { signal },
      );
      handleChangeGraph("query", query, true);
      setResultQuery(result.data);
      setErrorQuery("");
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (error instanceof AxiosError && error?.response?.data?.error) {
        setErrorQuery(error.response.data.error);

        handleChangeGraph("error", error.response.data.error);
      }
    }
  }

  useEffect(() => {
    async function fetchTables() {
      // if (!datasource) return;
      try {
        const result = await api.post("/editor/tables", {
          id: datasource?.id,
        });
        setTables(result.data);
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error?.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      }
    }

    fetchTables();
  }, [datasource]);

  useEffect(() => {
    async function fetchDataSources() {
      try {
        const response = await api.get("/datasource/findMany");
        setDataSources(response.data);
        const findDataSourceDefault = response.data.find(
          (os: RowsDataSource) => os.default,
        );
        if (findDataSourceDefault && !datasource) {
          setDatasource(findDataSourceDefault);
          handleChangeGraph("dataSource", {
            id: findDataSourceDefault?.id,
          });
        }
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      }
    }
    fetchDataSources();
  }, []);
  const DB_ICONS: Record<string, React.ReactNode> = {
    MARIADB: <MariaDB style={{ width: 22, height: 22, fill: "#C0765A" }} />,
    MYSQL: <MySql style={{ width: 22, height: 22, fill: "#F29111" }} />,
    POSTGRES: <Postgres style={{ width: 22, height: 22 }} />,
  };
  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.default,
        width: "100%",
        height: "100%",
        borderRadius: "1rem",
        padding: 2,
        overflow: "hidden",
      }}
    >
      {open && (
        <VirtualizedSimpleTreeView
          rows={getFilteredRows}
          filterTable={filterTable}
          setFilterTable={setFilterTable}
          open={open}
          key={"virtualizedSimpleTreeView-executeQuery"}
        />
      )}
      <Box
        sx={{
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              flexDirection: "row",
              width: "100%",
              alignItems: "center",
              overflowX: "auto",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                onClick={(e) => setOpen(!open)}
                size="large"
                disableRipple
                disableTouchRipple
              >
                <img src={dataBase} alt="ícone banco de dados" />
              </IconButton>
              <Typography>SQL</Typography>
            </Stack>
            <Autocomplete
              options={dataSources}
              value={
                dataSources.find(
                  (os) => String(os.id) === String(datasource?.id),
                ) || null
              }
              getOptionLabel={(option) => String(option.name)}
              onChange={(e, newValue) => {
                handleChangeGraph("dataSource", {
                  id: newValue?.id,
                });
                setDatasource(
                  newValue
                    ? {
                        id: newValue.id,
                      }
                    : null,
                );
              }}
              sx={{
                width: "100%",
                minWidth: 200,
                maxWidth: 240,
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: datasource ? "8px" : "14px",
                  backgroundColor: theme.palette.background.paper,
                },
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    py: 1,
                    width: "100%",
                  }}
                >
                  {DB_ICONS[option.enumType] || (
                    <Postgres style={{ width: 22, height: 22 }} />
                  )}
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              )}
              renderInput={(params) => {
                const selectedOption = dataSources.find(
                  (os) => String(os.id) === String(datasource?.id),
                );
                return (
                  <TextField
                    {...params}
                    placeholder="Selecione o data source"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: selectedOption && (
                          <Box
                            sx={{
                              ml: 1,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {DB_ICONS[selectedOption.enumType]}
                          </Box>
                        ),
                      },
                    }}
                    sx={{
                      backgroundColor: theme.palette.background.textField,
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": {
                        paddingLeft: datasource ? "8px" : "14px",
                        height: "40px",
                        "& fieldset": { borderColor: "transparent" },
                        "&:hover fieldset": { borderColor: "transparent" },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                );
              }}
            />
          </Box>
          <Button
            startIcon={<PlayArrowOutlined />}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.primary.main,
              borderRadius: "5px",
            }}
            onClick={() => executeQuery()}
            disabled={!query || query.trim() === ""}
          >
            Run
          </Button>
        </Box>

        <QueryEditor
          query={initialQuery ? initialQuery : query}
          setQuery={setQuery}
          tables={tables}
          handleChangeGraph={handleChangeGraph}
          height={"100%"}
        />
      </Box>
    </Box>
  );
};

export default ExecuteQuery;
